import { Add, Delete } from '@mui/icons-material'
import { Button, Divider, IconButton, ListSubheader } from '@mui/material'
import React, { ReactNode, Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import { DateTime } from 'luxon'
import { AlertSeverity, ContactMethodType, WeekdayFilter } from '../../schema'
import { GenericError, ObjectNotFound } from '../error-pages'
import CompList from '../lists/CompList'
import { CompListItemText } from '../lists/CompListItems'
import { useIsWidthDown } from '../util/useWidth'
import ServiceNotificationRuleCreateDialog from './ServiceNotificationRuleCreateDialog'
import ServiceNotificationRuleDeleteDialog from './ServiceNotificationRuleDeleteDialog'
import { weekdaySummary } from '../schedules/util'

const query = gql`
  query serviceNRList($id: ID!) {
    service(id: $id) {
      id
      notificationRules {
        id
        delayMinutes
        contactMethodType
        alertSeverity
        notifyAnytime
        startTime
        endTime
        timezone
        weekdayFilter
      }
    }
  }
`

function formatDelay(delay: number): string {
  if (delay === 0) return 'Immediately'
  return `After ${delay} minute${delay === 1 ? '' : 's'}`
}

function formatTimeRange(r: NotificationRule): string {
  let summary = weekdaySummary(r.weekdayFilter)
  if (summary === 'every day') summary = 'Everyday'
  if (summary === 'weekends') summary = 'Weekends'

  if (r.notifyAnytime) {
    return `, ${summary}`
  }

  if (!r.startTime || !r.endTime) return ''
  const start = DateTime.fromISO(r.startTime).setZone(r.timezone).toFormat('t')
  const end = DateTime.fromISO(r.endTime).setZone(r.timezone).toFormat('t')
  return `, ${summary} between ${start} and ${end} (${r.timezone})`
}

function isMandatoryCriticalRule(r: NotificationRule): boolean {
  return (
    r.alertSeverity === 'CRITICAL' &&
    r.contactMethodType === 'VOICE' &&
    r.delayMinutes === 0
  )
}

interface NotificationRuleInput {
  contactMethodType: ContactMethodType
  alertSeverity: AlertSeverity
  delayMinutes: number
  notifyAnytime: boolean
  startTime: string
  endTime: string
  timezone: string
  weekdayFilter: WeekdayFilter
}

interface NotificationRule extends NotificationRuleInput {
  id: string
  deleted?: boolean
}

export default function ServiceNotificationRuleList(props: {
  serviceID: string
  readOnly?: boolean
  rules?: NotificationRule[]
  onCreate?: (rule: NotificationRuleInput) => void
  onDelete?: (id: string) => void
}): ReactNode {
  const mobile = useIsWidthDown('md')
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [deleteID, setDeleteID] = useState<string | null>(null)

  const [{ data, error }] = useQuery({
    query,
    variables: { id: props.serviceID },
    pause: !!props.rules,
  })

  if (error) return <GenericError error={error.message} />
  if (!props.rules && data && !data.service)
    return <ObjectNotFound type='service notification rules list' />

  const rules: NotificationRule[] =
    props.rules ?? data?.service?.notificationRules ?? []

  return (
    <React.Fragment>
      <CompList
        data-cy='notification-rules'
        emptyMessage='No notification rules'
        note='Configure how and when alerts are delivered to users based on severity.'
        action={
          !mobile ? (
            <Button
              title='Add Notification Rule'
              variant='contained'
              onClick={() => setShowAddDialog(true)}
              startIcon={<Add />}
              disabled={props.readOnly}
            >
              Add Rule
            </Button>
          ) : null
        }
      >
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']
          .filter(
            (severity) =>
              rules.filter((r) => r.alertSeverity === severity).length > 0,
          )
          .map((severity, index) => {
            const severityRules = rules
              .filter((r) => r.alertSeverity === severity)
              .sort((a, b) => {
                const aMandatory = isMandatoryCriticalRule(a)
                const bMandatory = isMandatoryCriticalRule(b)
                if (aMandatory && !bMandatory) return -1
                if (!aMandatory && bMandatory) return 1
                return 0
              })

            return (
              <React.Fragment key={severity}>
                {index > 0 && <Divider component='li' />}
                <ListSubheader sx={{ bgcolor: 'background.paper' }}>
                  {severity} Severity
                </ListSubheader>
                {severityRules.map((nr) => (
                  <CompListItemText
                    key={nr.id}
                    title={
                      <span
                        style={{
                          textDecoration: nr.deleted
                            ? 'line-through'
                            : undefined,
                        }}
                      >
                        {`${formatDelay(nr.delayMinutes)} notify via ${
                          nr.contactMethodType
                        }${formatTimeRange(nr)}`}
                      </span>
                    }
                    highlight={nr.id.startsWith('temp-') || !!nr.deleted}
                    action={
                      props.readOnly ||
                      nr.deleted ||
                      isMandatoryCriticalRule(nr) ? null : (
                        <IconButton
                          aria-label='Delete notification rule'
                          onClick={() => {
                            if (props.onDelete) {
                              props.onDelete(nr.id)
                            } else {
                              setDeleteID(nr.id)
                            }
                          }}
                          color='secondary'
                          size='large'
                        >
                          <Delete />
                        </IconButton>
                      )
                    }
                  />
                ))}
              </React.Fragment>
            )
          })}
      </CompList>
      <Suspense>
        {showAddDialog && (
          <ServiceNotificationRuleCreateDialog
            serviceID={props.serviceID}
            onClose={() => setShowAddDialog(false)}
            onSubmit={props.onCreate}
          />
        )}
        {deleteID && (
          <ServiceNotificationRuleDeleteDialog
            ruleID={deleteID}
            onClose={() => setDeleteID(null)}
          />
        )}
      </Suspense>
    </React.Fragment>
  )
}
